import { useState } from 'react';
import { Button, CircularProgress, Link } from '@mui/material';
import CommonListTable, { CommonListColumn } from '../Common/CommonListTable';
import CallButton from '../Common/CallButton/CallButton';
import { UserType } from '../../enums/common';
import { ButtonContainer } from '../../styles/commonStyles';
import { CookService } from '../../services/CookServices';
import EventBus from '../../EventBus';
import { EventBusEventsEnum } from '../../enums/EventBusEventsEnum';
import { CustomSnackbarTypes } from '../Common/CustomSnackBar';

const cookService = new CookService();

const getVisitLink = (referenceModelName: string, referenceId: number) =>
    referenceModelName === 'ONE_MEAL_SERVICE'
        ? `/#/OneMealService/${referenceId}/show`
        : `/#/ChefForParty/${referenceId}/show`;

const RatingListForModule = (props: any) => {
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [page, setPage] = useState(0);

    const handleCookDelete = async (e: React.MouseEvent<HTMLElement>, ratingId: number) => {
        e.preventDefault();
        setDeletingId(ratingId);
        try {
            await cookService.deleteCookRating(ratingId);
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: 'Rating deleted successfully',
                type: CustomSnackbarTypes.SUCCESS,
            });
            props.refreshData(0);
        } catch (err: any) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: err?.response?.data?.error || 'Failed to delete rating',
                type: CustomSnackbarTypes.ERROR,
            });
        } finally {
            setDeletingId(null);
        }
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
        props.refreshData(newPage);
    };

    const handleRowsPerPageChange = (rows: number) => {
        props.setPageSize(rows);
        setPage(0);
        props.refreshData(0);
    };

    const columns: CommonListColumn[] = [
        { label: 'ID', field: 'id', sortable: true, sortField: 'id' },
        {
            label: 'Customer Name',
            render: (row) => `${row?.customer?.fname ?? ''} ${row?.customer?.lname ?? ''}`.trim() || '-',
        },
        {
            label: 'Customer Phone',
            stopRowClick: true,
            render: (row) => (
                <CallButton
                    toPhoneNumber={row?.customer?.mobile}
                    userType={UserType.CUSTOMER}
                    referenceModelName={row?.referenceModelName}
                    referenceId={row?.referenceId}
                />
            ),
        },
        { label: 'Overall Rating', field: 'overallRating', sortable: true, sortField: 'overallRating' },
        {
            label: 'Visit Id',
            sortable: true,
            sortField: 'referenceId',
            render: (row) => (
                <Link href={getVisitLink(row?.referenceModelName, row?.referenceId)}>
                    {row?.referenceId}
                </Link>
            ),
        },
        { label: 'Visit Type', field: 'referenceModelName' },
        {
            label: 'Action',
            stopRowClick: true,
            render: (row) => {
                if (row?.deletedAt) {
                    return (
                        <Link href={`/#/AdminUsers/${row?.deletedById}/show`}>
                            <span style={{ color: '#d32f2f', fontWeight: 600 }}>{row?.deletedById ?? 'N/A'}</span>
                        </Link>
                    );
                }
                return (
                    <ButtonContainer>
                        <Link href={getVisitLink(row?.referenceModelName, row?.referenceId)}>
                            <Button variant="contained" color="primary" size="small">Show</Button>
                        </Link>
                        <Button
                            variant="contained"
                            color="error"
                            size="small"
                            disabled={deletingId === row?.id}
                            onClick={(e) => handleCookDelete(e, row?.id)}
                        >
                            {deletingId === row?.id ? <CircularProgress size={18} color="inherit" /> : 'Delete'}
                        </Button>
                    </ButtonContainer>
                );
            },
        },
    ];

    return (
        <CommonListTable
            columns={columns}
            data={props?.data ?? []}
            loading={props?.isLoading}
            page={page}
            rowsPerPage={props?.pageSize}
            total={props?.count ?? 0}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            emptyText="No Ratings found for this cook"
            sort={props?.sort}
            onSortChange={props?.onSortChange}
        />
    );
};

export default RatingListForModule;
